import { getRoot } from '/@/root'
import { MapDoodleType } from '/@/constants/map'
import { MapDoodleEnum } from '/@/types/map-enum'
import { EFlightAreaType } from '../types/flight-area'
import { message } from 'ant-design-vue'

function wrapDrawFeature (feature: any, extData: any, draw: any) {
  const props = { ...feature.properties, ...extData }

  return {
    id: feature.id,
    geometry: feature.geometry,
    properties: props,

    getExtData: () => ({ ...props }),
    setExtData: (data: any) => { Object.assign(props, data) },

    getPosition: () => {
      const [lng, lat] = feature.geometry.coordinates
      return { lng, lat }
    },
    getPath: () => {
      if (feature.geometry.type === 'Polygon') {
        return feature.geometry.coordinates[0]
          .slice(0, -1)
          .map(([lng, lat]: number[]) => ({ lng, lat }))
      }
      return feature.geometry.coordinates.map(([lng, lat]: number[]) => ({ lng, lat }))
    },
    getCenter: () => {
      if (feature.geometry.type === 'Polygon') {
        const coords: number[][] = feature.geometry.coordinates[0]
        const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length
        const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length
        return { lng, lat }
      }
      const [lng, lat] = feature.geometry.coordinates
      return { lng, lat }
    },
    getRadius: () => props.radius || 0,
    remove: () => {
      try { draw.delete(feature.id) } catch (_) {}
    },
  }
}

function circleCoords (center: [number, number], radiusMeters: number, steps = 64): [number, number][] {
  const [lng, lat] = center
  const earthRadius = 6371000
  const coords: [number, number][] = []
  for (let i = 0; i <= steps; i++) {
    const angle = (i * 2 * Math.PI) / steps
    const dlat = (radiusMeters * Math.cos(angle)) / earthRadius * (180 / Math.PI)
    const dlng = (radiusMeters * Math.sin(angle)) / (earthRadius * Math.cos(lat * Math.PI / 180)) * (180 / Math.PI)
    coords.push([lng + dlng, lat + dlat])
  }
  return coords
}

function haversineDistance (a: [number, number], b: [number, number]): number {
  const R = 6371000
  const dLat = (b[1] - a[1]) * Math.PI / 180
  const dLng = (b[0] - a[0]) * Math.PI / 180
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
}

export function useMouseTool () {
  const root = getRoot()
  let currentDrawHandler: ((e: any) => void) | null = null
  let cleanupCircle: (() => void) | null = null

  let polygonRightClickHandler: ((e: any) => void) | null = null

  function activateDraw (mode: string, extData: any, onDraw: Function) {
    const map = root.$map
    const draw = root.$draw
    if (!map || !draw) return

    draw.changeMode(mode)

    if (currentDrawHandler) {
      map.off('draw.create', currentDrawHandler)
    }

    currentDrawHandler = (e: any) => {
      if (e.features && e.features.length > 0) {
        if (polygonRightClickHandler) {
          map.off('click', polygonRightClickHandler)
          polygonRightClickHandler = null
        }
        const wrapped = wrapDrawFeature(e.features[0], extData, draw)
        const drawResult = { obj: wrapped }
        onDraw(drawResult)
      }
    }

    map.once('draw.create', currentDrawHandler)

    // Double-click on first vertex to close polygon
    if (mode === 'draw_polygon') {
      if (polygonRightClickHandler) {
        map.off('click', polygonRightClickHandler)
      }
      let lastClickNearFirst = 0
      polygonRightClickHandler = (e: any) => {
        const features = draw.getAll()?.features
        if (!features || features.length === 0) return
        const coords = features[features.length - 1]?.geometry?.coordinates?.[0]
        if (!coords || coords.length < 4) return
        const first = coords[0]
        const clickPx = map.project(e.lngLat)
        const firstPx = map.project({ lng: first[0], lat: first[1] })
        const dist = Math.hypot(clickPx.x - firstPx.x, clickPx.y - firstPx.y)
        if (dist < 15) {
          const now = Date.now()
          if (now - lastClickNearFirst < 400) {
            draw.changeMode('simple_select')
            lastClickNearFirst = 0
          } else {
            lastClickNearFirst = now
          }
        } else {
          lastClickNearFirst = 0
        }
      }
      map.on('click', polygonRightClickHandler)
    }
  }

  function activateCircleDraw (onDraw: Function) {
    const map = root.$map
    const draw = root.$draw
    if (!map) return

    // Clean up any lingering draw.create handler from a previous tool
    if (currentDrawHandler) {
      map.off('draw.create', currentDrawHandler)
      currentDrawHandler = null
    }
    // Ensure MapboxDraw is not in an active drawing mode
    if (draw) draw.changeMode('simple_select')

    const PREVIEW_SOURCE = '__circle-preview__'
    const PREVIEW_FILL = '__circle-preview-fill__'
    const PREVIEW_LINE = '__circle-preview-line__'
    const CENTER_SOURCE = '__circle-center__'
    const CENTER_LAYER = '__circle-center-dot__'

    let center: [number, number] | null = null
    let clickHandler: ((e: any) => void) | null = null

    const emptyPolygon = { type: 'Feature' as const, properties: {}, geometry: { type: 'Polygon' as const, coordinates: [[]] } }
    const emptyPoint = { type: 'Feature' as const, properties: {}, geometry: { type: 'Point' as const, coordinates: [0, 0] } }

    map.addSource(PREVIEW_SOURCE, { type: 'geojson', data: emptyPolygon })
    map.addLayer({ id: PREVIEW_FILL, type: 'fill', source: PREVIEW_SOURCE, paint: { 'fill-color': '#2D8CF0', 'fill-opacity': 0.15 } })
    map.addLayer({ id: PREVIEW_LINE, type: 'line', source: PREVIEW_SOURCE, paint: { 'line-color': '#2D8CF0', 'line-width': 2, 'line-dasharray': [4, 2] } })
    map.addSource(CENTER_SOURCE, { type: 'geojson', data: emptyPoint })
    map.addLayer({ id: CENTER_LAYER, type: 'circle', source: CENTER_SOURCE, paint: { 'circle-radius': 5, 'circle-color': '#2D8CF0', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } })

    map.getCanvas().style.cursor = 'crosshair'

    function cleanup () {
      map.getCanvas().style.cursor = ''
      if (clickHandler) { map.off('click', clickHandler); clickHandler = null }
      map.off('mousemove', onMouseMove)
      if (map.getLayer(PREVIEW_FILL)) map.removeLayer(PREVIEW_FILL)
      if (map.getLayer(PREVIEW_LINE)) map.removeLayer(PREVIEW_LINE)
      if (map.getLayer(CENTER_LAYER)) map.removeLayer(CENTER_LAYER)
      if (map.getSource(PREVIEW_SOURCE)) map.removeSource(PREVIEW_SOURCE)
      if (map.getSource(CENTER_SOURCE)) map.removeSource(CENTER_SOURCE)
      cleanupCircle = null
    }

    function onMouseMove (e: any) {
      if (!center) return
      const radius = haversineDistance(center, [e.lngLat.lng, e.lngLat.lat])
      ;(map.getSource(PREVIEW_SOURCE) as any).setData({
        type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [circleCoords(center, radius)] }
      })
    }

    cleanupCircle = cleanup

    // Single listener with internal state to avoid same-event double-fire
    let phase: 'first' | 'second' = 'first'
    function onMapClick (e: any) {
      if (phase === 'first') {
        center = [e.lngLat.lng, e.lngLat.lat]
        phase = 'second'
        ;(map.getSource(CENTER_SOURCE) as any).setData({
          type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: center }
        })
        map.on('mousemove', onMouseMove)
      } else {
        map.off('click', onMapClick)
        map.off('mousemove', onMouseMove)
        const pt: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        const radius = Math.round(haversineDistance(center!, pt))
        const savedCenter = center!
        map.getCanvas().style.cursor = ''
        onDraw({
          obj: {
            getPosition: () => ({ lng: savedCenter[0], lat: savedCenter[1] }),
            getRadius: () => radius,
            remove: () => {},
          },
          suggestedRadius: radius,
          confirm: (finalRadius: number) => {
            ;(map.getSource(PREVIEW_SOURCE) as any)?.setData({
              type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [circleCoords(savedCenter, finalRadius)] }
            })
            cleanup()
          },
          cancel: () => cleanup(),
        })
      }
    }
    clickHandler = onMapClick
    map.on('click', onMapClick)
  }

  function drawOff () {
    const map = root.$map
    const draw = root.$draw

    if (cleanupCircle) {
      cleanupCircle()
      cleanupCircle = null
    }

    if (!map || !draw) return

    if (polygonRightClickHandler) {
      map.off('dblclick', polygonRightClickHandler)
      polygonRightClickHandler = null
    }

    draw.changeMode('simple_select')

    if (currentDrawHandler) {
      map.off('draw.create', currentDrawHandler)
      currentDrawHandler = null
    }
  }

  function mouseTool (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    if (type === MapDoodleEnum.Close) {
      drawOff()
      return
    }

    if (flightAreaType) {
      const extData = { type: flightAreaType, mapType: 'polygon' }
      activateDraw('draw_polygon', extData, getDrawCallback)
      return
    }

    switch (type) {
      case MapDoodleEnum.PIN:
        activateDraw('draw_point', { mapType: 'pin' }, getDrawCallback)
        break
      case MapDoodleEnum.POLYLINE:
        activateDraw('draw_line_string', { mapType: 'polyline' }, getDrawCallback)
        break
      case MapDoodleEnum.POLYGON:
        activateDraw('draw_polygon', { mapType: 'polygon' }, getDrawCallback)
        break
      case MapDoodleEnum.CIRCLE:
        activateCircleDraw(getDrawCallback)
        break
      default:
        message.error(`Type de dessin non supporté: ${type}`)
    }
  }

  return {
    mouseTool,
  }
}
